// Anfragen-Zentrale: Anliegen „E-Mail“ – reine Funktionen für Server UND Oberfläche (docs/anfragen-zentrale/
// SPEZIFIKATION.md §5, §6). Keine Datenbank, keine Uhr (der heutige Tag kommt als Argument).
//
//   extractLines(text)               Mengenzeilen: „10 Kisten Helles“, „Helles: 20 Kästen“, „Art.-Nr. AL-10010 x 5“,
//                                    „Bitte liefern Sie 3 Fass Pils und 2 Kisten Spezi“ – Menge, Gebinde-Wort, Artikeltext,
//                                    Artikelnummer bzw. EAN
//   matchLine(zeile, katalog)        Abgleich mit den Artikeln: eindeutig · mehrdeutig (Kandidaten) · nicht erkannt
//   extractDates(text, heute)        Termine (14.11., 14.11.2026, 14. November, morgen, Freitag) mit Art Lieferung/Feier
//   extractGuests · extractVenue · extractPhone · extractCustomerNumber · eventTitle
//
// Was erkannt wird, ist ein VORSCHLAG: Die Oberfläche zeigt jede Zeile mit ihrem Stand, Mehrdeutiges wird markiert und
// von Hand gewählt. Automatisch übernommen wird nur, was eindeutig ist (§7).
import { addDays, toIsoDate, weekdayOf, type IsoDate } from '@shared/time'

export const MAIL_REQUEST_FEATURE = 'mailrequests'

// ---- Einordnung ----------------------------------------------------------------------------------------------------

export const MAIL_CATEGORIES = ['order', 'event', 'branch', 'other'] as const
export type MailCategory = (typeof MAIL_CATEGORIES)[number]
export const MAIL_CATEGORY_LABELS: Record<MailCategory, string> = {
  order: 'Bestellung',
  event: 'Veranstaltung',
  branch: 'Filiale',
  other: 'Sonstiges',
}
export const MAIL_CATEGORY_SOURCES = ['rules', 'ai', 'manual'] as const
export type MailCategorySource = (typeof MAIL_CATEGORY_SOURCES)[number]
export const MAIL_CATEGORY_SOURCE_LABELS: Record<MailCategorySource, string> = {
  rules: 'nach Regeln',
  ai: 'Vorschlag der KI',
  manual: 'von Hand',
}
export const SENDER_AUTH_STATES = ['pass', 'fail', 'none'] as const
export type SenderAuth = (typeof SENDER_AUTH_STATES)[number]
export const SENDER_AUTH_LABELS: Record<SenderAuth, string> = {
  pass: 'vom Mailserver bestätigt',
  fail: 'Prüfung des Mailservers gescheitert',
  none: 'nicht geprüft',
}

// ---- Text ----------------------------------------------------------------------------------------------------------

/** klein, ohne Akzente (ä → a wie app_norm/unaccent), ß → ss, Leerraum zusammengefasst */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

const STOP = new Set([
  'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem', 'und', 'oder', 'mit', 'von', 'vom', 'fur', 'fuer', 'bitte', 'je', 'a', 'x',
  'zu', 'zum', 'zur', 'im', 'in', 'am', 'an', 'auf', 'aus', 'noch', 'sowie', 'plus', 'ca', 'circa', 'etwa', 'gerne', 'gern', 'auch', 'wieder', 'wie', 'immer',
  'liefern', 'liefert', 'lieferung', 'bestellen', 'bestelle', 'bestellung', 'brauchen', 'benotigen', 'benoetigen', 'wir', 'ich', 'uns', 'sie', 'ihr',
])

/** Wörter eines Artikeltextes: Zahl und Einheit getrennt („0,5l“ → 0,5 · l), Dezimalpunkt als Komma */
export function tokens(text: string): string[] {
  return normalize(text)
    .replace(/(\d)\.(\d)/g, '$1,$2')
    .replace(/(\d)([a-z])/g, '$1 $2')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .split(/[^a-z0-9,]+/)
    .map((token) => token.replace(/^,+|,+$/g, ''))
    .filter((token) => token !== '' && !STOP.has(token))
}

// ---- Gebinde-Wörter ------------------------------------------------------------------------------------------------

/** Gebinde-Wort (normalisiert) → Einheitencode der Stammdaten */
const UNIT_WORDS: [RegExp, string][] = [
  [/^(kiste|kisten|kasten|kaesten|ka|kst|ks|kisterl|tragerl|trager|traeger|case|cases)$/, 'crate'],
  [/^(flasche|flaschen|fl|fla|btl)$/, 'bottle'],
  [/^(fass|faesser|fasser|fassl|fassle|faessl|fasschen|faesschen|keg|kegs|fassbier)$/, 'keg'],
  [/^(stuck|stueck|stk|st|stucke|stueckl)$/, 'piece'],
  [/^(karton|kartons|krt|tray|trays|steige|steigen)$/, 'carton'],
  [/^(palette|paletten|pal)$/, 'pallet'],
  [/^(dose|dosen)$/, 'can'],
  [/^(liter|l|ltr)$/, 'l'],
  [/^(kg|kilo|kilogramm)$/, 'kg'],
  [/^(garnitur|garnituren|anlage|anlagen|set|sets|satz)$/, 'piece'],
]

export function unitCodeOf(word: string | null | undefined): string | null {
  if (!word) return null
  const key = normalize(word).replace(/\.$/, '')
  for (const [pattern, code] of UNIT_WORDS) if (pattern.test(key)) return code
  return null
}

const UNIT_PATTERN =
  '(?:kisten|kiste|kästen|kasten|kaesten|ka\\.?|kst\\.?|ks\\.?|kisterl|tragerl|träger|trager|flaschen|flasche|fl\\.?|fässer|faesser|fass|fässchen|fäßchen|fassl|fäßl|keg|kegs|stück|stueck|stk\\.?|st\\.|kartons|karton|krt\\.?|trays|tray|steigen|steige|paletten|palette|pal\\.?|dosen|dose|liter|ltr\\.?|l|kg|kilo|garnituren|garnitur)'

const NUMBER_WORDS: Record<string, number> = {
  ein: 1, eine: 1, einen: 1, einem: 1, zwei: 2, drei: 3, vier: 4, fuenf: 5, funf: 5, sechs: 6, sieben: 7, acht: 8, neun: 9, zehn: 10, elf: 11, zwolf: 12, zwoelf: 12,
  zwanzig: 20, dreissig: 30, vierzig: 40, funfzig: 50, fuenfzig: 50, hundert: 100,
}

// ---- Mengenzeilen ------------------------------------------------------------------------------------------------

export interface RawLine {
  /** die Zeile bzw. der Abschnitt, aus dem die Angabe stammt (gekürzt) */
  text: string
  /** Menge wie geschrieben in Tausendstel (1 Kasten = 1000); null = keine Menge angegeben */
  qty: number | null
  /** Gebinde-Wort wie geschrieben („Kisten“) */
  unitWord: string | null
  /** Einheitencode der Stammdaten (crate, bottle, keg, piece, carton, pallet, can, l, kg) */
  unitCode: string | null
  /** Text, der den Artikel beschreibt */
  itemText: string
  /** Artikelnummer bzw. EAN, falls genannt */
  code: string | null
  /** Menge stand als Zahlwort da („eine Zapfanlage“) – zählt nur mit eindeutigem Artikel */
  numberWord: boolean
}

const MAX_LINES = 60

const qtyOf = (raw: string): number | null => {
  const value = Number(raw.replace(',', '.'))
  if (!Number.isFinite(value) || value <= 0 || value > 9999) return null
  return Math.round(value * 1000)
}

const DATE_LIKE = /\b\d{1,2}\.\s?\d{1,2}\.(\d{2,4})?(?!\d)/g
const TIME_LIKE = /\b\d{1,2}[:.]\d{2}\s*(uhr|h)\b|\b\d{1,2}\s*uhr\b/gi
const PHONE_LIKE = /(?:tel\.?|telefon|mobil|handy|fax)\s*[:.]?\s*[+\d][\d\s/()-]{5,}/gi
// nicht hinter Bindestrich oder Buchstaben: „AL-10010 Augustiner“ ist eine Artikelnummer, keine Postleitzahl
const ZIP_CITY = /(?<![\w-])\d{5}\s+[A-ZÄÖÜ][\wäöüß-]+/g

const CODE_PATTERN = /(?:art(?:ikel)?[\s.-]*(?:nr|nummer)\.?|artnr\.?|sku|best(?:ell)?[\s.-]*nr\.?)\s*[:#]?\s*([A-Za-z0-9][A-Za-z0-9-]{1,23})/i
const EAN_PATTERN = /(?:\b(?:ean|gtin)\s*[:#]?\s*)?(?<![\d.,])(\d{13}|\d{8})(?![\d.,])/i

/** Füllwörter am Anfang/Ende eines Artikeltextes */
function cleanItemText(text: string): string {
  return text
    // Nebensatz abschneiden: „Zapfanlage für einen Kunden“, „Helles bis Freitag“, „Spezi zum Fest am Samstag“
    .replace(/\s+(?:für|fuer|zum|zur|bis|ab|nach|bei|wegen|damit|weil|aber|sowie|am|an|auf)\s.*$/i, '')
    .replace(/^[\s:–—-]+|[\s:–—,;.!?-]+$/g, '')
    .replace(/^(?:x|×|\*|à|a|je|zu|von|vom|mal)(?:\s+|$)/i, '')
    .replace(/\s+(?:bitte|liefern|zustellen|bringen|schicken|senden)(?:\s.*)?$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160)
}

/** Artikelnummer am Zeilenanfang ohne Vorwort – „W-070 Mineralwasser 8 Kästen“ (Tabellen, Bestelllisten): Buchstaben und Ziffern */
const LEADING_CODE = /^((?=[A-Za-z0-9-]*[A-Za-z])(?=[A-Za-z0-9-]*\d)[A-Za-z]{1,5}-?\d{2,8}(?:-[A-Za-z0-9]{1,8})?)\s+(?=\S)/

function codeOf(text: string): { code: string | null; rest: string } {
  const match = CODE_PATTERN.exec(text)
  if (match) return { code: match[1] as string, rest: text.replace(match[0], ' ') }
  const ean = EAN_PATTERN.exec(text)
  if (ean) return { code: ean[1] as string, rest: text.replace(ean[0], ' ') }
  const lead = LEADING_CODE.exec(text.trim())
  if (lead) return { code: lead[1] as string, rest: text.trim().slice(lead[0].length) }
  return { code: null, rest: text }
}

/** Mengen eines Gebindes („8 Kästen“) zählen vor Inhaltsangaben („0,7 l“) */
const PACKAGE_CODES = new Set(['crate', 'bottle', 'keg', 'piece', 'carton', 'pallet', 'can'])

/** Ein Abschnitt („10 Kisten Helles“) → Angabe oder null */
function parseSegment(segment: string, original: string): RawLine | null {
  const { code, rest } = codeOf(segment)
  const text = rest.replace(/\s+/g, ' ').trim()
  const unit = UNIT_PATTERN
  // Artikelnummer bzw. EAN genannt: die erste Zahl daneben ist die Menge – „Art.-Nr. AL-10010 x 6“, „EAN … – 2 Kisten“
  if (code) {
    const any = new RegExp(`(?:^|[\\s:=–—x×*-])(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*(?:x|×)?\\s*(?:(${unit})(?![a-zäöüß])\\.?)?`, 'gi')
    const all = [...text.matchAll(any)].filter((found) => qtyOf(found[1] as string) !== null)
    const qtyMatch = all.find((found) => PACKAGE_CODES.has(unitCodeOf(found[2]) ?? '')) ?? all[0] ?? null
    if (qtyMatch && qtyOf(qtyMatch[1] as string) !== null) {
      return { text: original.slice(0, 200), qty: qtyOf(qtyMatch[1] as string), unitWord: qtyMatch[2] ?? null, unitCode: unitCodeOf(qtyMatch[2]), itemText: cleanItemText(text.replace(qtyMatch[0], ' ')), code, numberWord: false }
    }
    return { text: original.slice(0, 200), qty: null, unitWord: null, unitCode: null, itemText: cleanItemText(text), code, numberWord: false }
  }
  // A: Menge zuerst – „10 Kisten Helles“, „10x Helles“, „10 × Kasten Helles“
  const first = new RegExp(`^(?:ca\\.?\\s*)?(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*(?:x|×|\\*|mal)?\\s*(?:(${unit})(?![a-zäöüß])\\.?)?\\s*(.*)$`, 'i').exec(text)
  if (first && qtyOf(first[1] as string) !== null) {
    const itemText = cleanItemText(first[3] ?? '')
    if (itemText !== '' || code) {
      return { text: original.slice(0, 200), qty: qtyOf(first[1] as string), unitWord: first[2] ?? null, unitCode: unitCodeOf(first[2]), itemText, code, numberWord: false }
    }
  }
  // C: Menge mit Gebinde-Wort mitten im Satz – „Bitte liefern Sie uns 3 Fass Pils“
  const inner = new RegExp(`(?:^|\\s)(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*(?:x|×)?\\s*(${unit})(?![a-zäöüß])\\.?\\s+(.+)$`, 'i').exec(text)
  if (inner && qtyOf(inner[1] as string) !== null) {
    const itemText = cleanItemText(inner[3] as string)
    if (itemText !== '') return { text: original.slice(0, 200), qty: qtyOf(inner[1] as string), unitWord: inner[2] as string, unitCode: unitCodeOf(inner[2]), itemText, code, numberWord: false }
  }
  // B: Artikel zuerst, Menge am Ende – „Helles: 20 Kisten“, „Spezi 0,5 l – 12“
  const last = new RegExp(`^(.*?\\S)\\s*[:=–—-]?\\s+(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*(?:x|×)?\\s*(?:(${unit})(?![a-zäöüß])\\.?)?$`, 'i').exec(text)
  if (last && qtyOf(last[2] as string) !== null) {
    const itemText = cleanItemText(last[1] as string)
    // „Helles 0,5 l“ allein ist keine Menge: ohne Gebinde-Wort nur mit Trenner davor („Helles: 12“, „Helles – 12“)
    const separated = /[:=–—-]\s*\d/.test(text) || /\bx\s*\d|\d\s*x\b/i.test(text)
    if (itemText !== '' && (last[3] || separated || code)) {
      return { text: original.slice(0, 200), qty: qtyOf(last[2] as string), unitWord: last[3] ?? null, unitCode: unitCodeOf(last[3]), itemText, code, numberWord: false }
    }
  }
  // D: Zahlwort – „eine Zapfanlage“, „zwei Bierzeltgarnituren“ (zählt nur mit eindeutigem Artikel)
  const word = /(?:^|\s)(ein|eine|einen|zwei|drei|vier|fünf|fuenf|sechs|sieben|acht|neun|zehn|elf|zwölf|zwoelf|zwanzig|dreißig|dreissig|vierzig|fünfzig|fuenfzig|hundert)\s+(.+)$/i.exec(text)
  if (word) {
    const value = NUMBER_WORDS[normalize(word[1] as string)]
    const itemText = cleanItemText(word[2] as string)
    if (value && itemText !== '') return { text: original.slice(0, 200), qty: value * 1000, unitWord: null, unitCode: null, itemText, code, numberWord: true }
  }
  return null
}

/** Mengenzeilen eines E-Mail-Textes. Zitate früherer Nachrichten („> …“) und Signaturen nach „-- “ zählen nicht. */
export function extractLines(text: string): RawLine[] {
  const out: RawLine[] = []
  for (const rawLine of ownText(text).split('\n')) {
    if (out.length >= MAX_LINES) break
    let line = rawLine.trim()
    if (line === '' || line.length > 240) continue
    // Aufzählungszeichen und Nummerierung („1. 10 Kisten …“, „- 10 Kisten …“)
    line = line.replace(/^[-*•·–]+\s*/, '').replace(/^\d{1,2}[.)]\s+(?=\d)/, '')
    // Kopfzeilen weitergeleiteter bzw. zitierter Nachrichten („Datum: Mon, 26 Oct 2026 …“) sind keine Angaben
    if (HEADER_LINE.test(line)) continue
    // Datum, Uhrzeit, Telefon, PLZ + Ort sind keine Mengen
    const masked = line.replace(DATE_LIKE, ' ').replace(TIME_LIKE, ' ').replace(PHONE_LIKE, ' ').replace(ZIP_CITY, ' ')
    // mehrere Angaben in einer Zeile: „10 Kisten Helles, 5 Kisten Pils und 2 Fass Spezi“
    // (ein Komma zwischen Ziffern ist ein Dezimalkomma: „0,5 l“ bleibt zusammen)
    const segments = masked.split(/\s*(?:,(?=\s)|(?<!\d),|;|\bund\b|\bsowie\b|\+|&)\s*(?=\d|(?:ein|eine|einen|zwei|drei|vier|fünf|sechs)\s)/i)
    for (const segment of segments) {
      const parsed = parseSegment(segment, line)
      if (parsed) out.push(parsed)
    }
  }
  return out
}

const HEADER_LINE = /^(?:von|from|an|to|cc|bcc|datum|date|gesendet|sent|betreff|subject|antwort an|reply-to)\s*:/i

/**
 * Absender weitergeleiteter bzw. zitierter Nachrichten („Von: Gasthaus <einkauf@…>“) – nur als Vorschlag für den Kunden, nie
 * als eindeutige Zuordnung: Diese Zeilen kann jeder in einen Text schreiben.
 */
export function forwardedSenders(text: string): string[] {
  const found: string[] = []
  for (const line of text.split('\n')) {
    const match = /^\s*(?:von|from)\s*:\s*.*?<?([^\s<>"'@]+@[^\s<>"']+\.[a-z]{2,})>?\s*$/i.exec(line)
    if (match && !found.includes((match[1] as string).toLowerCase())) found.push((match[1] as string).toLowerCase())
    if (found.length >= 3) break
  }
  return found
}

/** Eigener Text der Nachricht: ohne zitierte Zeilen und ohne Signatur bzw. weitergeleitete Altnachricht darunter */
export function ownText(text: string): string {
  const lines = text.split('\n')
  const out: string[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed === '--' || trimmed === '-- ' || /^-{2,}\s*(urspr(ü|ue)ngliche|original) nachricht/i.test(trimmed) || /^am .{3,80} schrieb .{1,80}:$/i.test(trimmed)) break
    if (trimmed.startsWith('>')) continue
    out.push(line)
  }
  return out.join('\n')
}

// ---- Artikel abgleichen --------------------------------------------------------------------------------------------

export interface CatalogUnit {
  id: string
  /** Einheitencode der Stammdaten (crate, bottle …) */
  code: string
  label: string | null
  baseQty: number
  isSalesDefault: boolean
  isBase: boolean
  sellable: boolean
}

export interface CatalogItem {
  id: string
  sku: string
  name: string
  kind: string
  units: CatalogUnit[]
  barcodes: { code: string; unitId: string }[]
}

export interface LineCandidate {
  itemId: string
  sku: string
  name: string
  /** 0…100 */
  score: number
}

export interface LineMatch {
  status: 'unique' | 'ambiguous' | 'none'
  itemId: string | null
  unitId: string | null
  matchedBy: 'sku' | 'ean' | 'name' | null
  candidates: LineCandidate[]
}

function levenshtein1(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false
  let i = 0
  let j = 0
  let edits = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1
      j += 1
      continue
    }
    edits += 1
    if (edits > 1) return false
    if (a.length > b.length) i += 1
    else if (b.length > a.length) j += 1
    else {
      i += 1
      j += 1
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1
}

const tokenMatches = (query: string, name: string): boolean =>
  query === name ||
  (query.length >= 4 && name.length >= 4 && (name.startsWith(query) || query.startsWith(name))) ||
  (query.length >= 5 && name.length >= 5 && levenshtein1(query, name))

const GENERIC = new Set(['glas', 'pet', 'dose', 'l', 'ml', 'cl', 'kg', 'g', 'im', 'mehrweg', 'einweg', 'fass', 'keg', 'kasten', 'kiste', 'flasche'])

/** Gebinde des Artikels zur Angabe: genanntes Gebinde-Wort, sonst Verkaufs-Standard, sonst Basiseinheit */
export function unitFor(item: CatalogItem, unitCode: string | null): CatalogUnit | null {
  const sellable = item.units.filter((unit) => unit.sellable)
  if (unitCode) {
    const same = sellable.filter((unit) => unit.code === unitCode)
    return same.find((unit) => unit.isSalesDefault) ?? same[0] ?? null
  }
  return sellable.find((unit) => unit.isSalesDefault) ?? sellable.find((unit) => unit.isBase) ?? sellable[0] ?? null
}

/** Abgleich einer Angabe mit den Artikeln – eindeutig nur, wenn ein Artikel klar vorne liegt. */
export function matchLine(line: RawLine, catalog: readonly CatalogItem[]): LineMatch {
  const none: LineMatch = { status: 'none', itemId: null, unitId: null, matchedBy: null, candidates: [] }
  if (line.code) {
    const code = line.code.toLowerCase()
    const bySku = catalog.filter((item) => item.sku.toLowerCase() === code)
    if (bySku.length === 1) {
      const item = bySku[0] as CatalogItem
      return { status: 'unique', itemId: item.id, unitId: unitFor(item, line.unitCode)?.id ?? null, matchedBy: 'sku', candidates: [{ itemId: item.id, sku: item.sku, name: item.name, score: 100 }] }
    }
    for (const item of catalog) {
      const barcode = item.barcodes.find((entry) => entry.code === line.code)
      if (barcode) return { status: 'unique', itemId: item.id, unitId: barcode.unitId, matchedBy: 'ean', candidates: [{ itemId: item.id, sku: item.sku, name: item.name, score: 100 }] }
    }
  }
  const query = tokens(line.itemText).filter((token) => unitCodeOf(token) === null || /^\d/.test(token))
  if (query.length === 0) return none
  const scored = catalog
    .map((item) => {
      const name = tokens(item.name)
      if (name.length === 0) return null
      const matched = query.filter((q) => name.some((n) => tokenMatches(q, n))).length
      const score = matched / query.length
      if (score < 0.5) return null
      const meaningful = name.filter((n) => !GENERIC.has(n) && !/^[\d,]+$/.test(n))
      const covered = meaningful.filter((n) => query.some((q) => tokenMatches(q, n))).length
      const coverage = meaningful.length === 0 ? 1 : covered / meaningful.length
      let final = score * 0.85 + coverage * 0.15
      // passt das genannte Gebinde? („Kisten“ bei einem Fass-Artikel nicht)
      if (line.unitCode && line.unitCode !== 'l' && line.unitCode !== 'kg') final += unitFor(item, line.unitCode) ? 0.1 : -0.3
      // eine Zahl im Text („0,5“, „30“) muss im Namen stehen
      if (query.some((q) => /^\d/.test(q) && !name.includes(q))) final -= 0.25
      return { item, score, final }
    })
    .filter((entry): entry is { item: CatalogItem; score: number; final: number } => entry !== null)
    .sort((a, b) => b.final - a.final || a.item.name.localeCompare(b.item.name, 'de'))
  const top = scored[0]
  if (!top || top.final < 0.45) return none
  const candidates = scored.slice(0, 5).map((entry) => ({ itemId: entry.item.id, sku: entry.item.sku, name: entry.item.name, score: Math.round(Math.max(0, Math.min(1, entry.final)) * 100) }))
  const second = scored[1]
  const clear = top.score === 1 && top.final >= 0.6 && (!second || top.final - second.final >= 0.08)
  if (!clear) return { status: 'ambiguous', itemId: null, unitId: null, matchedBy: 'name', candidates }
  return { status: 'unique', itemId: top.item.id, unitId: unitFor(top.item, line.unitCode)?.id ?? null, matchedBy: 'name', candidates }
}

// ---- Termine ---------------------------------------------------------------------------------------------------------

export interface FoundDate {
  date: IsoDate
  /** bis (Zeitraum „14.–16.11.“) */
  until: IsoDate | null
  /** Lieferung, Feier oder unklar – nach den Wörtern der Zeile */
  kind: 'delivery' | 'event' | 'unknown'
  text: string
}

const MONTHS: Record<string, number> = {
  januar: 1, jan: 1, februar: 2, feb: 2, marz: 3, maerz: 3, mar: 3, april: 4, apr: 4, mai: 5, juni: 6, jun: 6, juli: 7, jul: 7, august: 8, aug: 8,
  september: 9, sep: 9, sept: 9, oktober: 10, okt: 10, november: 11, nov: 11, dezember: 12, dez: 12,
}
const WEEKDAYS: Record<string, number> = { montag: 1, dienstag: 2, mittwoch: 3, donnerstag: 4, freitag: 5, samstag: 6, sonnabend: 6, sonntag: 7 }

const DELIVERY_WORDS = /\b(liefer\w*|zustell\w*|anliefer\w*|bring\w*|abhol\w*|bis\s+(zum|spätestens)|spätestens|benötig\w*|benoetig\w*|brauch\w*)\b/i
const EVENT_WORDS = /\b(fest\w*|feier\w*|hochzeit\w*|geburtstag\w*|veranstaltung\w*|party|jubil\w*|sommerfest|weihnachtsfeier|event|ausschank|vereinsfest|firmenfeier|polterabend|taufe|kommunion|konfirmation|grillfest|richtfest|einweihung|statt)\b/i

function validDate(year: number, month: number, day: number): IsoDate | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null
  return toIsoDate(year, month, day)
}

/** Ohne Jahr: das nächste Vorkommen ab heute */
function upcoming(today: IsoDate, month: number, day: number, year: number | null): IsoDate | null {
  if (year !== null) return validDate(year < 100 ? 2000 + year : year, month, day)
  const thisYear = Number(today.slice(0, 4))
  const candidate = validDate(thisYear, month, day)
  if (candidate && candidate >= today) return candidate
  return validDate(thisYear + 1, month, day)
}

/** Termine im eigenen Text der Nachricht, in Reihenfolge des Auftretens */
export function extractDates(text: string, today: IsoDate): FoundDate[] {
  const found: FoundDate[] = []
  const add = (date: IsoDate | null, until: IsoDate | null, line: string): void => {
    if (!date || found.length >= 10) return
    const kind = EVENT_WORDS.test(line) ? 'event' : DELIVERY_WORDS.test(line) ? 'delivery' : 'unknown'
    found.push({ date, until: until && until > date ? until : null, kind, text: line.trim().slice(0, 160) })
  }
  for (const line of ownText(text).split('\n')) {
    const lower = normalize(line)
    // 14.11.2026 · 14.11. · 14.–16.11.2026 · 14. bis 16.11.
    const numeric = /(?<![\d.])(\d{1,2})\.(?:\s*(?:[-–]|bis)\s*(\d{1,2})\.)?\s?(\d{1,2})\.(\d{4}|\d{2}(?!\d))?/g
    for (const match of line.matchAll(numeric)) {
      const year = match[4] ? Number(match[4]) : null
      const month = Number(match[3])
      const date = upcoming(today, month, Number(match[1]), year)
      const until = match[2] ? upcoming(today, month, Number(match[2]), year) : null
      add(date, until, line)
    }
    // 14. November (2026)
    for (const match of lower.matchAll(/(?<!\d)(\d{1,2})\.?\s*(januar|jan|februar|feb|marz|maerz|mar|april|apr|mai|juni|jun|juli|jul|august|aug|september|sept|sep|oktober|okt|november|nov|dezember|dez)\b\.?\s*(\d{4})?/g)) {
      add(upcoming(today, MONTHS[match[2] as string] ?? 0, Number(match[1]), match[3] ? Number(match[3]) : null), null, line)
    }
    // 2026-11-14
    for (const match of line.matchAll(/\b(20\d{2})-(\d{2})-(\d{2})\b/g)) add(validDate(Number(match[1]), Number(match[2]), Number(match[3])), null, line)
    // heute · morgen · übermorgen · (nächsten) Freitag
    if (/\bubermorgen\b|\buebermorgen\b/.test(lower)) add(addDays(today, 2), null, line)
    else if (/\bmorgen\b/.test(lower) && !/\bguten morgen\b/.test(lower)) add(addDays(today, 1), null, line)
    for (const match of lower.matchAll(/\b(?:(nachsten|naechsten|kommenden|diesen)\s+)?(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonnabend|sonntag)\b/g)) {
      const target = WEEKDAYS[match[2] as string] as number
      let delta = (target - weekdayOf(today) + 7) % 7
      if (delta === 0) delta = 7
      add(addDays(today, delta), null, line)
    }
  }
  return found
}

// ---- Veranstaltung: Gäste, Ort, Kontakt, Titel ----------------------------------------------------------------------

export function extractGuests(text: string): number | null {
  const match = /(?:(?:mit|für|ca\.?|circa|etwa|rund|ungefähr)\s+)?(\d{1,4})\s*(?:gäste|gästen|gaeste|gaesten|personen|pers\.|leute|leuten|besucher\w*|teilnehmer\w*|gedecke)\b/i.exec(ownText(text))
  const value = match ? Number(match[1]) : NaN
  return Number.isInteger(value) && value > 0 && value <= 20_000 ? value : null
}

export interface FoundVenue {
  street: string | null
  postalCode: string | null
  city: string | null
  note: string | null
}

export function extractVenue(text: string): FoundVenue | null {
  const own = ownText(text)
  const labelled = /^\s*(?:ort|veranstaltungsort|location|adresse|lieferadresse|lieferanschrift|anschrift|wo)\s*:\s*(.+)$/im.exec(own)
  const street = /([A-ZÄÖÜ][\wäöüß.-]*(?:straße|strasse|str\.|weg|platz|gasse|allee|ring|ufer|damm|steig|anger|berg|feld|hof)\s+\d{1,4}\s?[a-z]?)\b/.exec(labelled?.[1] ?? own)
  const zipCity = /\b(\d{5})\s+([A-ZÄÖÜ][\wäöüß.-]+(?:\s(?:am|an|im|bei|b\.)\s[A-ZÄÖÜ][\wäöüß.-]+)?)/.exec(labelled?.[1] ?? own)
  if (!labelled && !street && !zipCity) return null
  return {
    street: street?.[1]?.trim() ?? null,
    postalCode: zipCity?.[1] ?? null,
    city: zipCity?.[2]?.trim() ?? null,
    note: labelled ? (labelled[1] as string).trim().slice(0, 200) : null,
  }
}

export function extractPhone(text: string): string | null {
  const match = /(?:tel\.?|telefon|mobil|handy|rückruf\w*)\s*(?:nr\.?|nummer)?\s*[:.]?\s*(\+?[\d][\d\s/()-]{5,20}\d)/i.exec(text)
  return match ? (match[1] as string).replace(/\s+/g, ' ').trim() : null
}

export function extractCustomerNumber(text: string): string | null {
  const match = /(?:kunden[\s-]*(?:nr|nummer)\.?|kd\.?[\s-]*nr\.?|kundennr\.?)\s*[:#]?\s*([A-Za-z]{0,3}-?\d{3,10})/i.exec(text)
  return match ? (match[1] as string).toUpperCase() : null
}

const EVENT_KINDS: [RegExp, string][] = [
  [/\bhochzeit/i, 'Hochzeit'],
  [/\bpolterabend/i, 'Polterabend'],
  [/\bgeburtstag/i, 'Geburtstagsfeier'],
  [/\bsommerfest/i, 'Sommerfest'],
  [/\bweihnachtsfeier/i, 'Weihnachtsfeier'],
  [/\bfirmenfeier|\bfirmenfest|\bbetriebsfeier/i, 'Firmenfeier'],
  [/\bvereinsfest|\bvereinsfeier/i, 'Vereinsfest'],
  [/\bjubil/i, 'Jubiläum'],
  [/\btaufe|\bkommunion|\bkonfirmation/i, 'Familienfeier'],
  [/\bgrillfest|\bgrillabend/i, 'Grillfest'],
  [/\brichtfest/i, 'Richtfest'],
]

/** Titel einer Veranstaltung: aus dem Betreff (ohne AW/WG/Fwd), sonst aus der Art und dem Datum */
export function eventTitle(subject: string, text: string, date: IsoDate | null): string {
  const cleaned = subject.replace(/^(?:\s*(?:aw|wg|fw|fwd|re|antw|anfrage|frage)\s*:\s*)+/i, '').trim()
  if (cleaned.length >= 4) return cleaned.slice(0, 120)
  const kind = EVENT_KINDS.find(([pattern]) => pattern.test(`${subject} ${text}`))?.[1] ?? 'Veranstaltung'
  return date ? `${kind} am ${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}` : kind
}
